/**
 * Campaign hand-over to external target domains (AP-270). The shop's own
 * hosts are excluded even when they are subdomains of a target (AP-297).
 *
 * A visitor who lands in the shop from an ad carries click IDs and UTM values
 * in the URL (gclid/gbraid/wbraid from Google Ads, msclkid from Microsoft
 * Ads, utm_*). When they later follow a link to one of the configured
 * target domains (settings.attribution_domains, e.g. the manufacturer's own
 * website with its configurator), those values travel along, so an inquiry
 * made there can still be credited to the original campaign. Without
 * campaign values the link gets fixed, anonymous UTM values that name the
 * shop as source (utm_source from settings, utm_medium=referral,
 * utm_campaign from data-hy-attribution-campaign or "link", utm_content =
 * product handle or page path).
 *
 * Consent (CLAUDE.md rule 10): click IDs and campaign values are only kept
 * and passed on with marketing consent (window.HY.consent). The theme
 * setting attribution_without_consent lifts that — off by default; a shop
 * that turns it on decides so on its own legal responsibility (documented
 * exception, ROADMAP AP-270). The fixed UTM values carry no personal data
 * and are always added.
 *
 * Storage: sessionStorage only (tab lifetime), wrapped in try/catch — the
 * hand-over is a convenience, never business-critical (rule 7).
 *
 * Pattern exception (CLAUDE.md JS): no custom element. Like hy-consent.js
 * this is a page-lifetime bootstrap with one delegated listener, not a
 * component with its own markup.
 */

const CONFIG_ELEMENT = document.querySelector('script[data-hy-attribution]');
const STORAGE_KEY = 'hy:attribution';
const CAMPAIGN_KEYS = [
  'gclid', 'gbraid', 'wbraid', 'msclkid',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id',
];
const DEFAULT_CAMPAIGN = 'link';

function readConfig(element) {
  if (!element) return null;
  const domains = (element.dataset.domains || '')
    .split(',')
    .map((d) => d.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, ''))
    .filter(Boolean);
  if (domains.length === 0) return null;
  // The shop's own hosts are never a target (AP-297): a shop on a subdomain
  // of a target domain (shop.example.com with target example.com) would
  // otherwise tag every internal link with UTM values and start a new
  // analytics session on each click.
  const ownHosts = new Set(
    [window.location.hostname, ...(element.dataset.ownHosts || '').split(',')]
      .map((h) => h.trim().toLowerCase())
      .filter(Boolean),
  );
  return {
    domains,
    ownHosts,
    source: element.dataset.source || 'shop',
    content: element.dataset.content || '',
    withoutConsent: element.dataset.withoutConsent === 'true',
  };
}

function marketingAllowed(config) {
  if (config.withoutConsent) return true;
  const consent = window.HY && window.HY.consent;
  return Boolean(consent && consent.allowed('marketing'));
}

function readStored() {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function store(values) {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // Storage blocked or full: the hand-over simply falls back to fixed UTM.
  }
}

function forget() {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked: nothing was stored either.
  }
}

function campaignFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const values = {};
  CAMPAIGN_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) values[key] = value;
  });
  return Object.keys(values).length > 0 ? values : null;
}

function isTarget(url, config) {
  const host = url.hostname.toLowerCase();
  if (config.ownHosts.has(host)) return false;
  return config.domains.some((domain) => host === domain || host.endsWith(`.${domain}`));
}

function decorate(link, config, pending) {
  let url;
  try {
    url = new URL(link.href, window.location.href);
  } catch {
    return;
  }
  if (!isTarget(url, config)) return;

  const campaign = marketingAllowed(config) ? (readStored() || pending) : null;
  const hasUtm = [...url.searchParams.keys()].some((key) => key.startsWith('utm_'));

  if (campaign) {
    Object.entries(campaign).forEach(([key, value]) => {
      if (!url.searchParams.has(key)) url.searchParams.set(key, value);
    });
  } else if (!hasUtm) {
    url.searchParams.set('utm_source', config.source);
    url.searchParams.set('utm_medium', 'referral');
    url.searchParams.set('utm_campaign', link.dataset.hyAttributionCampaign || DEFAULT_CAMPAIGN);
    url.searchParams.set('utm_content', config.content || window.location.pathname);
  }
  link.href = url.toString();
}

function init() {
  const config = readConfig(CONFIG_ELEMENT);
  if (!config) return;

  // Campaign values of the landing page. Kept in memory until consent is
  // known, then stored for the rest of the tab session.
  let pending = campaignFromUrl();
  // consentKnown: only after hy-consent.js has read the visitor's choice may
  // a missing consent delete stored values — before that, "not allowed" just
  // means "not read yet" and would wipe the previous page's campaign.
  const persist = (consentKnown) => {
    if (!marketingAllowed(config)) {
      if (consentKnown) forget();
      return;
    }
    if (pending) {
      store(pending);
      pending = null;
    }
  };
  persist(false);
  const consent = window.HY && window.HY.consent;
  if (consent && consent.ready) consent.ready.then(() => persist(true));
  document.addEventListener('hy:consent:updated', () => persist(true));

  // pointerdown runs before a middle click or a new-tab click opens the
  // link; click covers the keyboard. Decorating twice is harmless: present
  // parameters are never overwritten.
  const handle = (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (link) decorate(link, config, pending);
  };
  document.addEventListener('pointerdown', handle, true);
  document.addEventListener('click', handle, true);
}

init();
